import React from 'react'
import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import { BTC_ID } from './amm'
import { readHistory, type SwapRecord } from './history'
import { strictFetcher } from './strict-fetcher'

/// One thing the wallet did, in the vocabulary every history surface shares.
///
/// Three sources feed it, and each row says which so the UI can show a
/// pending row differently from a settled one:
///   * `chain`   — the ledger's permanent classified rows: exact deltas, the
///                 FULL history (mints, transfers, LP, wraps — not only the
///                 swaps the activity window happened to catch);
///   * `mempool` — the address's own actions esplora sees unconfirmed right
///                 now, decoded by the same classifier as the activity tab;
///   * `local`   — the record this browser wrote at broadcast, shown the
///                 instant the wallet signs, before any node has relayed it.
export interface HistoryLeg {
	id: string
	/// Base units (8dp), always positive; direction is the field it's in.
	qty: bigint
}

export interface HistoryRow {
	txid: string
	/// Epoch ms, or null while a mempool row has no block time.
	ts: number | null
	height: number | null
	kind: 'buy' | 'sell' | 'mint' | 'receive' | 'send' | 'lp' | 'wrap' | 'unwrap' | 'transfer'
	pending: boolean
	source: 'chain' | 'mempool' | 'local'
	/// What left the wallet / what arrived. A mempool or local row's `in`
	/// is the route's floor or the signed quote, not a fill — `floor: true`.
	out: HistoryLeg[]
	in: HistoryLeg[]
	floor: boolean
}

interface ServerRow {
	txid: string
	height: number | null
	ts: number | null
	own_input: boolean
	kind: HistoryRow['kind']
	deltas: { id: string; delta: string }[]
}

interface PendingEvent {
	txid: string
	kind: 'swap' | 'wrap' | 'wrap_swap_leg' | 'unwrap' | 'transfer'
	path: string[] | null
	amount_in: string | null
	min_out: string | null
	amount_sats: number | null
	ts: number | null
}

interface HistoryBody {
	address: string
	pending: PendingEvent[]
	rows: ServerRow[]
	more: boolean
	truncated: boolean
}

const FRBTC_ID = '32:0'

function big(s: string | null | undefined): bigint {
	if (!s) return 0n
	try {
		return BigInt(s)
	} catch {
		return 0n
	}
}

function fromServerRow(r: ServerRow): HistoryRow {
	const out: HistoryLeg[] = []
	const inn: HistoryLeg[] = []
	for (const d of r.deltas ?? []) {
		const v = big(d.delta)
		if (v < 0n) out.push({ id: d.id, qty: -v })
		else if (v > 0n) inn.push({ id: d.id, qty: v })
	}
	return {
		txid: r.txid,
		ts: r.ts ? r.ts * 1000 : null,
		height: r.height,
		kind: r.kind,
		pending: false,
		source: 'chain',
		out,
		in: inn,
		floor: false
	}
}

function fromPending(e: PendingEvent): HistoryRow | null {
	const base = {
		txid: e.txid,
		ts: e.ts ? e.ts * 1000 : null,
		height: null,
		pending: true,
		source: 'mempool' as const,
		floor: true
	}
	if (e.kind === 'swap' && e.path && e.path.length >= 2) {
		const sellId = e.path[0]
		const buyId = e.path[e.path.length - 1]
		return {
			...base,
			kind: sellId === FRBTC_ID ? 'buy' : buyId === FRBTC_ID ? 'sell' : 'buy',
			out: e.amount_in ? [{ id: sellId, qty: big(e.amount_in) }] : [],
			in: e.min_out ? [{ id: buyId, qty: big(e.min_out) }] : []
		}
	}
	if (e.kind === 'wrap' || e.kind === 'wrap_swap_leg') {
		const sats = BigInt(Math.max(0, Math.floor(e.amount_sats ?? 0)))
		return {
			...base,
			kind: 'wrap',
			out: sats > 0n ? [{ id: BTC_ID, qty: sats }] : [],
			in: sats > 0n ? [{ id: FRBTC_ID, qty: sats }] : []
		}
	}
	if (e.kind === 'unwrap') {
		const amt = big(e.amount_in)
		return {
			...base,
			kind: 'unwrap',
			out: amt > 0n ? [{ id: FRBTC_ID, qty: amt }] : [],
			in: amt > 0n ? [{ id: BTC_ID, qty: amt }] : []
		}
	}
	return { ...base, kind: 'transfer', out: [], in: [] }
}

function fromLocal(h: SwapRecord): HistoryRow {
	const kind: HistoryRow['kind'] =
		h.kind === 'wrap'
			? 'wrap'
			: h.kind === 'unwrap'
			? 'unwrap'
			: h.sellId === BTC_ID || h.sellId === FRBTC_ID
			? 'buy'
			: 'sell'
	return {
		txid: h.txid,
		ts: h.ts || null,
		height: null,
		kind,
		pending: true,
		source: 'local',
		out: [{ id: h.sellId, qty: big(h.amountInRaw) }],
		in: [{ id: h.buyId, qty: big(h.quotedOutRaw) }],
		floor: true
	}
}

/// Merge the three sources by txid: chain wins (settled, exact), then the
/// mempool's decode, then this browser's own record. Newest first, pending
/// on top — the row you just made is the one you are looking for.
export function mergeHistory(
	rows: ServerRow[],
	pending: PendingEvent[],
	local: SwapRecord[]
): HistoryRow[] {
	const byId = new Map<string, HistoryRow>()
	for (const h of local) {
		byId.set(h.txid, fromLocal(h))
	}
	for (const e of pending) {
		const row = fromPending(e)
		if (row) {
			// Keep the browser's better legs (exact signed amounts, the
			// quoted out) but adopt the mempool's confirmation of relay.
			const mine = byId.get(e.txid)
			byId.set(e.txid, mine ? { ...mine, source: 'mempool' } : row)
		}
	}
	for (const r of rows) {
		byId.set(r.txid, fromServerRow(r))
	}
	return [...byId.values()].sort((a, b) => {
		if (a.pending !== b.pending) return a.pending ? -1 : 1
		return (b.ts ?? Number.MAX_SAFE_INTEGER) - (a.ts ?? Number.MAX_SAFE_INTEGER)
	})
}

/// The address's full history, live. Polls faster while anything is
/// pending, and refetches the moment this browser records a broadcast.
/// `local` records are only merged for the connected wallet's own address —
/// another wallet's profile has no browser records to show.
export default function useHistory(
	address: string | null,
	includeLocal: boolean
): {
	rows: HistoryRow[]
	pendingCount: number
	more: boolean
	truncated: boolean
	unavailable: boolean
	isLoading: boolean
	refresh: () => void
} {
	const [local, setLocal] = React.useState<SwapRecord[]>(() =>
		includeLocal ? readHistory(address) : []
	)
	// Account switched: the old account's browser records go with it.
	const [localFor, setLocalFor] = React.useState(address)
	if (localFor !== address) {
		setLocalFor(address)
		setLocal(includeLocal ? readHistory(address) : [])
	}
	const { data, error, isLoading, mutate } = useSwr<HistoryBody>(
		address ? `${API_URL}/alkane/address/${address}/history` : null,
		strictFetcher,
		{
			revalidateOnFocus: true,
			refreshInterval: (latest) => (latest && latest.pending.length > 0 ? 10_000 : 60_000)
		}
	)
	React.useEffect(() => {
		if (!includeLocal) return
		const load = () => {
			setLocal(readHistory(address))
			// A broadcast just happened here: ask the mempool immediately.
			void mutate()
		}
		window.addEventListener('alkanes-history-changed', load)
		return () => window.removeEventListener('alkanes-history-changed', load)
	}, [includeLocal, address, mutate])

	const rows = React.useMemo(
		() => mergeHistory(data?.rows ?? [], data?.pending ?? [], includeLocal ? local : []),
		[data, local, includeLocal]
	)
	return {
		rows,
		pendingCount: rows.filter((r) => r.pending).length,
		more: data?.more ?? false,
		truncated: data?.truncated ?? false,
		unavailable: error !== undefined && !data,
		isLoading,
		refresh: () => void mutate()
	}
}
